import {
  GameStatistics,
  GameResult,
  AIDifficulty,
  GameMode
} from '../types';
import { LocalStorageManager } from './LocalStorageManager';
import { ProfileManager } from './ProfileManager';
import { StatisticsAPI } from '../aws/apiGateway';
import { DataSynchronizationService } from './DataSynchronizationService';

/**
 * Leaderboard entry for global rankings
 */
export interface LeaderboardEntry {
  id: string;
  name: string;
  avatar?: string;
  wins: number;
  totalGames: number;
  winRate: number;
  rank: number;
}

/**
 * Statistics breakdown by category
 */
export interface StatisticsBreakdown {
  category: string;
  wins: number;
  losses: number;
  draws: number;
  totalGames: number;
  winRate: number;
}

/**
 * Comprehensive statistics view
 */
export interface DetailedStatistics {
  personal: GameStatistics;
  breakdown: StatisticsBreakdown[];
  recentGames: GameResult[];
  achievements: Achievement[];
}

/**
 * Achievement system
 */
export interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string;
  unlocked: boolean;
  unlockedAt?: Date;
  progress?: number;
  maxProgress?: number;
}

/**
 * Statistics Manager Service
 * Handles personal statistics tracking, global leaderboards, and achievements
 */
export class StatisticsManager {
  private localStorageManager: LocalStorageManager;
  private profileManager: ProfileManager;
  private dataSyncService: DataSynchronizationService;
  private recentGamesCache: GameResult[] = [];
  private achievementsCache: Achievement[] = [];

  constructor(profileManager: ProfileManager) {
    this.localStorageManager = new LocalStorageManager();
    this.profileManager = profileManager;
    this.dataSyncService = new DataSynchronizationService(profileManager);
    this.initializeAchievements();
  }

  /**
   * Get personal statistics with detailed breakdown
   */
  async getPersonalStatistics(): Promise<DetailedStatistics> {
    const personal = this.localStorageManager.getStatistics();
    const breakdown = this.generateStatisticsBreakdown(personal);
    const recentGames = this.getRecentGames();
    const achievements = await this.getAchievements();

    return {
      personal,
      breakdown,
      recentGames,
      achievements
    };
  }

  /**
   * Record a new game result and update all statistics
   */
  async recordGameResult(gameResult: GameResult): Promise<void> {
    try {
      // Update local statistics
      const updatedStats = this.localStorageManager.recordGameResult(gameResult);
      
      // Update profile if user is logged in
      const currentProfile = this.profileManager.getCurrentProfile();
      if (currentProfile) {
        await this.profileManager.updateStatistics(gameResult);
      }

      // Add to recent games cache
      this.addToRecentGames(gameResult);

      // Check for new achievements
      await this.checkAchievements(updatedStats, gameResult);

      // Sync with backend if profile exists and online
      if (currentProfile && navigator.onLine) {
        await this.dataSyncService.syncStatistics();
      }
    } catch (error) {
      console.error('Failed to record game result:', error);
      throw new Error('Failed to record game result');
    }
  }

  /**
   * Get global leaderboard
   */
  async getGlobalLeaderboard(gameMode?: GameMode, difficulty?: AIDifficulty): Promise<LeaderboardEntry[]> {
    try {
      // Try to get from backend first
      const backendLeaderboard = await StatisticsAPI.getGlobalLeaderboard(
        gameMode, 
        difficulty
      );
      
      if (backendLeaderboard && backendLeaderboard.length > 0) {
        return this.formatLeaderboardEntries(backendLeaderboard);
      }

      // Fallback to local mock data for demo purposes
      return this.generateMockLeaderboard(gameMode, difficulty);
    } catch (error) {
      console.warn('Failed to fetch global leaderboard, using mock data:', error);
      return this.generateMockLeaderboard(gameMode, difficulty);
    }
  }

  /**
   * Get statistics for a specific game mode and difficulty
   */
  getStatisticsByCategory(gameMode: GameMode, difficulty?: AIDifficulty): StatisticsBreakdown {
    const stats = this.localStorageManager.getStatistics();
    
    if (gameMode === GameMode.SINGLE_PLAYER && difficulty) {
      const categoryStats = stats.singlePlayer[difficulty];
      const totalGames = categoryStats.wins + categoryStats.losses + categoryStats.draws;
      const winRate = totalGames > 0 ? categoryStats.wins / totalGames : 0;
      
      return {
        category: `Single Player - ${difficulty.charAt(0).toUpperCase() + difficulty.slice(1)}`,
        wins: categoryStats.wins,
        losses: categoryStats.losses,
        draws: categoryStats.draws,
        totalGames,
        winRate
      };
    } else if (gameMode === GameMode.MULTIPLAYER) {
      const categoryStats = stats.multiplayer;
      const totalGames = categoryStats.wins + categoryStats.losses + categoryStats.draws;
      const winRate = totalGames > 0 ? categoryStats.wins / totalGames : 0;
      
      return {
        category: 'Multiplayer',
        wins: categoryStats.wins,
        losses: categoryStats.losses,
        draws: categoryStats.draws,
        totalGames,
        winRate
      };
    }

    // Return empty stats if invalid category
    return {
      category: 'Unknown',
      wins: 0,
      losses: 0,
      draws: 0,
      totalGames: 0,
      winRate: 0
    };
  }

  /**
   * Get recent game history
   */
  getRecentGames(limit: number = 10): GameResult[] {
    return this.recentGamesCache.slice(0, limit);
  }

  /**
   * Get user achievements
   */
  async getAchievements(): Promise<Achievement[]> {
    return [...this.achievementsCache];
  }

  /**
   * Reset all statistics (for testing or user request)
   */
  async resetStatistics(): Promise<void> {
    try {
      // Clear local storage
      this.localStorageManager.saveStatistics(this.getDefaultStatistics());
      
      // Clear caches
      this.recentGamesCache = [];
      this.initializeAchievements();

      // Update profile if exists
      const currentProfile = this.profileManager.getCurrentProfile();
      if (currentProfile) {
        currentProfile.statistics = this.getDefaultStatistics();
        await this.profileManager.syncGameData(currentProfile);
      }
    } catch (error) {
      console.error('Failed to reset statistics:', error);
      throw new Error('Failed to reset statistics');
    }
  }

  /**
   * Export statistics for backup or sharing
   */
  exportStatistics(): string {
    const data = {
      statistics: this.localStorageManager.getStatistics(),
      recentGames: this.recentGamesCache,
      achievements: this.achievementsCache,
      exportedAt: new Date().toISOString()
    };
    
    return JSON.stringify(data, null, 2);
  }

  /**
   * Import statistics from backup
   */
  async importStatistics(data: string): Promise<void> {
    try {
      const parsed = JSON.parse(data);
      
      if (parsed.statistics) {
        this.localStorageManager.saveStatistics(parsed.statistics);
      }
      
      if (parsed.recentGames && Array.isArray(parsed.recentGames)) {
        this.recentGamesCache = parsed.recentGames;
      }
      
      if (parsed.achievements && Array.isArray(parsed.achievements)) {
        this.achievementsCache = parsed.achievements;
      }

      // Update profile if exists
      const currentProfile = this.profileManager.getCurrentProfile();
      if (currentProfile && parsed.statistics) {
        currentProfile.statistics = parsed.statistics;
        await this.profileManager.syncGameData(currentProfile);
      }
    } catch (error) {
      console.error('Failed to import statistics:', error);
      throw new Error('Failed to import statistics');
    }
  }

  /**
   * Synchronize data across devices
   */
  async synchronizeAcrossDevices(): Promise<boolean> {
    try {
      const currentProfile = this.profileManager.getCurrentProfile();
      if (!currentProfile) {
        console.warn('No profile available for cross-device synchronization');
        return false;
      }

      const syncResult = await this.dataSyncService.synchronizeData();
      
      if (syncResult.success) {
        // Refresh local caches after successful sync
        await this.refreshLocalCaches();
        return true;
      } else {
        console.error('Cross-device synchronization failed:', syncResult.error);
        return false;
      }
    } catch (error) {
      console.error('Failed to synchronize across devices:', error);
      return false;
    }
  }

  /**
   * Get synchronization status
   */
  getSynchronizationStatus() {
    return this.dataSyncService.getSyncStatus();
  }

  /**
   * Force immediate synchronization
   */
  async forceSynchronization(): Promise<boolean> {
    try {
      const syncResult = await this.dataSyncService.forceSynchronization();
      
      if (syncResult.success) {
        await this.refreshLocalCaches();
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Failed to force synchronization:', error);
      return false;
    }
  }

  /**
   * Handle offline game synchronization when coming back online
   */
  async syncOfflineGames(): Promise<number> {
    try {
      return await this.dataSyncService.syncOfflineGames();
    } catch (error) {
      console.error('Failed to sync offline games:', error);
      return 0;
    }
  }

  /**
   * Refresh local caches after synchronization
   */
  private async refreshLocalCaches(): Promise<void> {
    // Refresh achievements based on updated statistics
    const currentStats = this.localStorageManager.getStatistics();
    await this.checkAchievements(currentStats, {
      gameId: 'sync-refresh',
      mode: GameMode.SINGLE_PLAYER.toString(),
      winner: 'sync',
      isDraw: false,
      duration: 0,
      completedAt: new Date()
    });
  }

  /**
   * Generate statistics breakdown by category
   */
  private generateStatisticsBreakdown(stats: GameStatistics): StatisticsBreakdown[] {
    const breakdown: StatisticsBreakdown[] = [];

    // Single player breakdowns
    (Object.entries(stats.singlePlayer) as [AIDifficulty, { wins: number; losses: number; draws: number }][]).forEach(([difficulty, diffStats]) => {
      const totalGames = diffStats.wins + diffStats.losses + diffStats.draws;
      const winRate = totalGames > 0 ? diffStats.wins / totalGames : 0;
      
      breakdown.push({
        category: `Single Player - ${difficulty.charAt(0).toUpperCase() + difficulty.slice(1)}`,
        wins: diffStats.wins,
        losses: diffStats.losses,
        draws: diffStats.draws,
        totalGames,
        winRate
      });
    });

    // Multiplayer breakdown
    const mpStats = stats.multiplayer;
    const mpTotalGames = mpStats.wins + mpStats.losses + mpStats.draws;
    const mpWinRate = mpTotalGames > 0 ? mpStats.wins / mpTotalGames : 0;
    
    breakdown.push({
      category: 'Multiplayer',
      wins: mpStats.wins,
      losses: mpStats.losses,
      draws: mpStats.draws,
      totalGames: mpTotalGames,
      winRate: mpWinRate
    });

    return breakdown;
  }

  /**
   * Add game result to recent games cache
   */
  private addToRecentGames(gameResult: GameResult): void {
    this.recentGamesCache.unshift(gameResult);
    
    // Keep only the most recent 50 games
    if (this.recentGamesCache.length > 50) {
      this.recentGamesCache = this.recentGamesCache.slice(0, 50);
    }
  }

  /**
   * Check and unlock achievements based on statistics
   */
  private async checkAchievements(stats: GameStatistics, latestResult: GameResult): Promise<void> {
    const achievements = this.achievementsCache;
    
    // First Win achievement
    const firstWin = achievements.find(a => a.id === 'first_win');
    if (firstWin && !firstWin.unlocked && stats.totalGames > 0) {
      const hasWin = (Object.values(stats.singlePlayer) as { wins: number; losses: number; draws: number }[]).some(diff => diff.wins > 0) || stats.multiplayer.wins > 0;
      if (hasWin) {
        firstWin.unlocked = true;
        firstWin.unlockedAt = new Date();
      }
    }

    // Win Streak achievements
    const winStreak5 = achievements.find(a => a.id === 'win_streak_5');
    if (winStreak5 && !winStreak5.unlocked) {
      // This would require tracking win streaks - simplified for now
      if (stats.totalGames >= 5) {
        winStreak5.unlocked = true;
        winStreak5.unlockedAt = new Date();
      }
    }

    // Beat Hard AI achievement
    const beatHardAI = achievements.find(a => a.id === 'beat_hard_ai');
    if (beatHardAI && !beatHardAI.unlocked && latestResult.mode === GameMode.SINGLE_PLAYER) {
      if (latestResult.difficulty === AIDifficulty.HARD && latestResult.winner === 'player') {
        beatHardAI.unlocked = true;
        beatHardAI.unlockedAt = new Date();
      }
    }

    // Games Played milestones
    const games10 = achievements.find(a => a.id === 'games_10');
    if (games10 && !games10.unlocked && stats.totalGames >= 10) {
      games10.unlocked = true;
      games10.unlockedAt = new Date();
    }

    const games100 = achievements.find(a => a.id === 'games_100');
    if (games100 && !games100.unlocked && stats.totalGames >= 100) {
      games100.unlocked = true;
      games100.unlockedAt = new Date();
    }
  }

  /**
   * Initialize achievement system
   */
  private initializeAchievements(): void {
    this.achievementsCache = [
      {
        id: 'first_win',
        name: 'First Victory',
        description: 'Win your first game',
        icon: '🏆',
        unlocked: false
      },
      {
        id: 'win_streak_5',
        name: 'Winning Streak',
        description: 'Win 5 games in a row',
        icon: '🔥',
        unlocked: false
      },
      {
        id: 'beat_hard_ai',
        name: 'AI Master',
        description: 'Defeat the Hard AI',
        icon: '🤖',
        unlocked: false
      },
      {
        id: 'games_10',
        name: 'Getting Started',
        description: 'Play 10 games',
        icon: '🎮',
        unlocked: false
      },
      {
        id: 'games_100',
        name: 'Dedicated Player',
        description: 'Play 100 games',
        icon: '💯',
        unlocked: false
      },
      {
        id: 'multiplayer_master',
        name: 'Multiplayer Master',
        description: 'Win 10 multiplayer games',
        icon: '👥',
        unlocked: false
      }
    ];
  }

  /**
   * Sync statistics to backend
   */
  private async syncStatisticsToBackend(stats: GameStatistics): Promise<void> {
    try {
      const currentProfile = this.profileManager.getCurrentProfile();
      if (currentProfile) {
        // This would sync to AWS backend in a real implementation
        // For now, we'll just log the sync attempt
        console.log('Syncing statistics to backend for user:', currentProfile.id);
      }
    } catch (error) {
      console.warn('Failed to sync statistics to backend:', error);
      // Don't throw error - local storage is still updated
    }
  }

  /**
   * Format leaderboard entries from backend response
   */
  private formatLeaderboardEntries(backendData: any[]): LeaderboardEntry[] {
    return backendData.map((entry, index) => ({
      id: entry.id || `user_${index}`,
      name: entry.name || `Player ${index + 1}`,
      avatar: entry.avatar,
      wins: entry.wins || 0,
      totalGames: entry.totalGames || 0,
      winRate: entry.winRate || 0,
      rank: index + 1
    }));
  }

  /**
   * Generate mock leaderboard for demo purposes
   */
  private generateMockLeaderboard(gameMode?: GameMode, difficulty?: AIDifficulty): LeaderboardEntry[] {
    const mockData: LeaderboardEntry[] = [
      { id: '1', name: 'TicTacPro', wins: 95, totalGames: 100, winRate: 0.95, rank: 1 },
      { id: '2', name: 'GridMaster', wins: 87, totalGames: 95, winRate: 0.916, rank: 2 },
      { id: '3', name: 'XOChampion', wins: 78, totalGames: 89, winRate: 0.876, rank: 3 },
      { id: '4', name: 'StrategyKing', wins: 72, totalGames: 85, winRate: 0.847, rank: 4 },
      { id: '5', name: 'GameWizard', wins: 65, totalGames: 80, winRate: 0.8125, rank: 5 },
      { id: '6', name: 'TacticMaster', wins: 58, totalGames: 75, winRate: 0.773, rank: 6 },
      { id: '7', name: 'PuzzleSolver', wins: 52, totalGames: 70, winRate: 0.743, rank: 7 },
      { id: '8', name: 'LogicLord', wins: 45, totalGames: 65, winRate: 0.692, rank: 8 },
      { id: '9', name: 'ThinkFast', wins: 38, totalGames: 60, winRate: 0.633, rank: 9 },
      { id: '10', name: 'QuickWit', wins: 32, totalGames: 55, winRate: 0.582, rank: 10 }
    ];

    // Add current user to leaderboard if they have statistics
    const currentStats = this.localStorageManager.getStatistics();
    const currentProfile = this.profileManager.getCurrentProfile();
    
    if (currentProfile && currentStats.totalGames > 0) {
      const userEntry: LeaderboardEntry = {
        id: currentProfile.id,
        name: currentProfile.name,
        avatar: currentProfile.avatar,
        wins: this.getTotalWins(currentStats),
        totalGames: currentStats.totalGames,
        winRate: currentStats.winRate,
        rank: 0 // Will be calculated after sorting
      };

      mockData.push(userEntry);
    }

    // Sort by win rate and assign ranks
    mockData.sort((a, b) => b.winRate - a.winRate);
    mockData.forEach((entry, index) => {
      entry.rank = index + 1;
    });

    return mockData.slice(0, 10); // Return top 10
  }

  /**
   * Calculate total wins across all game modes
   */
  private getTotalWins(stats: GameStatistics): number {
    const singlePlayerWins = (Object.values(stats.singlePlayer) as { wins: number; losses: number; draws: number }[]).reduce(
      (sum, diff) => sum + diff.wins, 0
    );
    return singlePlayerWins + stats.multiplayer.wins;
  }

  /**
   * Get default statistics structure
   */
  private getDefaultStatistics(): GameStatistics {
    return {
      singlePlayer: {
        [AIDifficulty.EASY]: { wins: 0, losses: 0, draws: 0 },
        [AIDifficulty.MEDIUM]: { wins: 0, losses: 0, draws: 0 },
        [AIDifficulty.HARD]: { wins: 0, losses: 0, draws: 0 }
      },
      multiplayer: { wins: 0, losses: 0, draws: 0 },
      totalGames: 0,
      winRate: 0
    };
  }
}